import { t } from "@/lib/i18n/interpolate";
import { wrapText } from "@/lib/tool-kit/charts/text";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { splitItems } from "@/lib/tool-kit/essay-templates/text";

/**
 * The Business Model Canvas (Osterwalder and Pigneur, *Business Model Generation*, 2010).
 *
 * The nine blocks and their arrangement follow the canvas Strategyzer publishes under a
 * Creative Commons Attribution-ShareAlike 3.0 licence: key partners, key activities and
 * key resources on the left, the value proposition in the middle, customer relationships,
 * channels and customer segments on the right, and cost structure and revenue streams
 * along the bottom. This module holds the geometry — which block sits where and how a
 * writer's lines wrap into it — so the layout is tested without a browser.
 */

export const BLOCKS = [
  "segments",
  "value",
  "channels",
  "relationships",
  "revenue",
  "resources",
  "activities",
  "partners",
  "costs",
] as const;

export type CanvasBlock = (typeof BLOCKS)[number];

export type CanvasInput = Record<CanvasBlock, string> & {
  name: string;
  designedFor: string;
  designedBy: string;
  date: string;
  version: string;
};

/* ---------------------------------------------------------------- geometry */

export const CANVAS_WIDTH = 1200;
export const CANVAS_HEIGHT = 940;

const MARGIN = 20;
const HEADER = 84;
const TOP_HEIGHT = 560;
const BOTTOM_HEIGHT = 190;
const COLUMN = (CANVAS_WIDTH - 2 * MARGIN) / 5;
const BODY_TOP = HEADER;
const BOTTOM_TOP = BODY_TOP + TOP_HEIGHT;

export interface CanvasRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Where each block sits, matching the published canvas. */
export const BLOCK_RECTS: Record<CanvasBlock, CanvasRect> = {
  partners: { x: MARGIN, y: BODY_TOP, width: COLUMN, height: TOP_HEIGHT },
  activities: {
    x: MARGIN + COLUMN,
    y: BODY_TOP,
    width: COLUMN,
    height: TOP_HEIGHT / 2,
  },
  resources: {
    x: MARGIN + COLUMN,
    y: BODY_TOP + TOP_HEIGHT / 2,
    width: COLUMN,
    height: TOP_HEIGHT / 2,
  },
  value: {
    x: MARGIN + 2 * COLUMN,
    y: BODY_TOP,
    width: COLUMN,
    height: TOP_HEIGHT,
  },
  relationships: {
    x: MARGIN + 3 * COLUMN,
    y: BODY_TOP,
    width: COLUMN,
    height: TOP_HEIGHT / 2,
  },
  channels: {
    x: MARGIN + 3 * COLUMN,
    y: BODY_TOP + TOP_HEIGHT / 2,
    width: COLUMN,
    height: TOP_HEIGHT / 2,
  },
  segments: {
    x: MARGIN + 4 * COLUMN,
    y: BODY_TOP,
    width: COLUMN,
    height: TOP_HEIGHT,
  },
  costs: {
    x: MARGIN,
    y: BOTTOM_TOP,
    width: (CANVAS_WIDTH - 2 * MARGIN) / 2,
    height: BOTTOM_HEIGHT,
  },
  revenue: {
    x: MARGIN + (CANVAS_WIDTH - 2 * MARGIN) / 2,
    y: BOTTOM_TOP,
    width: (CANVAS_WIDTH - 2 * MARGIN) / 2,
    height: BOTTOM_HEIGHT,
  },
};

/** Where the attribution starts: directly under the canvas, as the licence asks. */
export const FOOTER_TOP = BOTTOM_TOP + BOTTOM_HEIGHT + 14;

const PAD = 12;
const TITLE_BAND = 34;
const LINE_HEIGHT = 16;
const BULLET_INDENT = 12;
const ITEM_FONT = { fontSize: 13 } as const;
const MAX_LINES_PER_ITEM = 4;

export interface CanvasLine {
  text: string;
  /** The first line of an item carries the bullet. */
  bullet: boolean;
  /** Distance from the top of the block to the line's top. */
  top: number;
}

export interface BlockLayout {
  block: CanvasBlock;
  rect: CanvasRect;
  lines: CanvasLine[];
  /** Items that did not fit and are summarised on the last line. */
  hidden: number;
}

/** How many text lines a block's box has room for under its title. */
export function blockCapacity(block: CanvasBlock): number {
  return Math.max(
    1,
    Math.floor((BLOCK_RECTS[block].height - TITLE_BAND - PAD) / LINE_HEIGHT)
  );
}

/**
 * Wrap a block's items into the lines its box has room for. When they do not all fit,
 * whole items are dropped from the end — never half an item — and the last line says how
 * many are hidden; the text export always has every item.
 */
export function layoutBlock(
  block: CanvasBlock,
  items: readonly string[],
  moreText: (count: number) => string
): BlockLayout {
  const rect = BLOCK_RECTS[block];
  const width = rect.width - 2 * PAD - BULLET_INDENT;
  const capacity = blockCapacity(block);
  const wrapped = items.map((item) =>
    wrapText(item, width, ITEM_FONT, MAX_LINES_PER_ITEM)
  );
  const needed = wrapped.reduce((sum, lines) => sum + lines.length, 0);
  const budget = needed <= capacity ? capacity : capacity - 1;
  const lines: CanvasLine[] = [];
  let used = 0;
  let shown = 0;
  for (const itemLines of wrapped) {
    if (used + itemLines.length > budget) {
      break;
    }
    for (const [lineIndex, text] of itemLines.entries()) {
      lines.push({
        text,
        bullet: lineIndex === 0,
        top: TITLE_BAND + (used + lineIndex) * LINE_HEIGHT,
      });
    }
    used += itemLines.length;
    shown += 1;
  }
  const hidden = items.length - shown;
  if (hidden > 0) {
    lines.push({
      text: moreText(hidden),
      bullet: false,
      top: TITLE_BAND + used * LINE_HEIGHT,
    });
  }
  return { block, rect, lines, hidden };
}

export const CANVAS_TEXT = {
  pad: PAD,
  bulletIndent: BULLET_INDENT,
  lineHeight: LINE_HEIGHT,
  fontSize: ITEM_FONT.fontSize,
} as const;

/* ----------------------------------------------------------------- the text */

export interface CanvasLabels {
  defaultTitle: string;
  titleWithName: string;
  subtitle: string;
  blockNames: Record<CanvasBlock, string>;
  questions: Record<CanvasBlock, string>;
  prompts: Record<CanvasBlock, string>;
  fields: {
    designedFor: string;
    designedBy: string;
    date: string;
    version: string;
  };
  checksHeading: string;
  checksNote: string;
  checkEmpty: string;
  checkFilled: string;
  checkSegments: string;
  listSeparator: string;
  attributionHeading: string;
  attribution: string;
}

/** Blocks, in the order the writer is most likely to fill them, that have no lines. */
export function emptyBlocks(input: CanvasInput): CanvasBlock[] {
  return BLOCKS.filter((block) => splitItems(input[block]).length === 0);
}

export function buildCanvasDocument(
  input: CanvasInput,
  labels: CanvasLabels
): DocumentModel {
  const name = input.name.trim();
  const sections: DocSection[] = BLOCKS.map((block) => {
    const items = splitItems(input[block]);
    const body: DocBlock =
      items.length > 0
        ? {
            kind: "list",
            list: { style: "bullet", items: items.map((text) => ({ text })) },
          }
        : { kind: "prompt", text: labels.prompts[block] };
    return {
      heading: labels.blockNames[block],
      blocks: [{ kind: "guidance", text: labels.questions[block] }, body],
    };
  });
  const missing = emptyBlocks(input);
  const header = (
    [
      ["designedFor", input.designedFor],
      ["designedBy", input.designedBy],
      ["date", input.date],
      ["version", input.version],
    ] as const
  )
    .filter(([, value]) => value.trim() !== "")
    .map(([key, value]) => ({
      label: labels.fields[key],
      value: value.trim(),
    }));
  const checks: string =
    missing.length === 0
      ? labels.checkFilled
      : t(labels.checkEmpty, {
          names: missing
            .map((block) => labels.blockNames[block])
            .join(labels.listSeparator),
        });
  return {
    title: name ? t(labels.titleWithName, { name }) : labels.defaultTitle,
    subtitle: labels.subtitle,
    sections: [
      ...(header.length > 0
        ? [{ blocks: [{ kind: "fields", fields: header } as DocBlock] }]
        : []),
      ...sections,
      {
        heading: labels.checksHeading,
        blocks: [
          { kind: "guidance", text: labels.checksNote },
          { kind: "paragraph", text: checks },
          ...(splitItems(input.segments).length > 1
            ? [{ kind: "guidance", text: labels.checkSegments } as DocBlock]
            : []),
        ],
      },
      {
        heading: labels.attributionHeading,
        blocks: [{ kind: "paragraph", text: labels.attribution }],
      },
    ],
  };
}
